import React, { useState } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { Link, useLocation } from 'react-router-dom';
import { Accordion, Card } from 'react-bootstrap';
import { useAccordionToggle } from 'react-bootstrap/AccordionToggle';

import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';

import { constructDevSrc, transformSource } from '../../utils/helpers';
import ICropper from '../../interfaces/ICropper';
import Picture from '../Picture/Picture';

import './AgendaItem.sass';
import { ReactComponent as ClockIcon } from '../../assets/img/agenda/clock.svg';
import { ReactComponent as ArrowToggler } from '../../assets/img/agenda/arrow-toggler.svg';

type IAgendaItem = {
  readonly title?: string;
  readonly text?: string | null;
  readonly startTime?: string | null;
  readonly duration?: number;
  readonly isBreakLine?: boolean;
  readonly language?: string | null;
  readonly background?: boolean;
  readonly _id?: string;
  readonly track?: ReadonlyArray<string | null> | null;
  readonly type?: string;
  readonly speakers?: ReadonlyArray<{
    readonly speakerName?: string;
    readonly speakerPreviewDescription?: string | null;
    readonly _url?: string | null;
    readonly speakerImage?: ICropper;
    readonly facebookLink?: string | null;
    readonly linkedinLink?: string | null;
    readonly twitterLink?: string | null;
    readonly location?: {
      readonly country?: {
        readonly _url?: string | null;
      };
    };
  } | null> | null;
  readonly _parent?: {
    readonly _id?: string;
    readonly title?: string;
    readonly date?: unknown;
    readonly endDayTime?: unknown;
  } | null;
} | null;

interface Props {
  currentAgendaData: IAgendaItem;
}

const AgendaItem: React.FC<Props> = ({ currentAgendaData }) => {
  const [showMoreButton, setShoeMoreButton] = useState(true);
  const location = useLocation();

  const {
    title,
    _parent: parent,
    speakers,
    text,
    duration,
    type,
    language,
    startTime,
    track,
    isBreakLine,
    background,
  } = currentAgendaData || {};
  const hours = startTime?.split("T")[1]?.split(":")[0]
  const minutes = startTime?.split("T")[1]?.split(":")[1]

  const date = startTime ? (new Date(startTime)).toLocaleDateString() : 'Invalid date';

  return (
    <div className={(isBreakLine || background) ? "agenda-item agenda-item--gray" : "agenda-item"}>
      <div className="agenda-item__datetime">
        <div className="agenda-item__datetime-top">
          <div className="agenda-item__tags">
            {type && (
              <span className="agenda-item__tag agenda-item__tag--green">
								{type}
							</span>
            )
            }
            {language && (
              <span className="agenda-item__tag">{language}</span>
            )}
          </div>
          <div className="agenda-item__time">
            {hours?.toString()?.length === 1 ? `0${hours}` : hours?.toString()}:
            {minutes?.toString()?.length === 1
              ? `0${minutes}`
              : minutes?.toString()}
            <sub>UTC</sub>
            {hours ? null : (
              <span className="agenda-item__assigned">to be assigned</span>
            )}
          </div>
        </div>
        <div className="agenda-item__datetime-bottom">
          {location.pathname.includes('agenda') ? null : (

            <span className="agenda-item__day">
							{parent?.title} {hours && +hours !== 0 ? `/ ${date}` : null}
						</span>
          )}

          <div className="agenda-item__duration">
            <ClockIcon/>
            {duration} min
          </div>
        </div>
      </div>
      <div className="agenda-item__content">
        <div className="agenda-item__top">
          {track && (
            <div className="agenda-item__taglist">
              {track?.map((tag) => (
                <span className="agenda-item__main-tag" key={tag}>
									{tag}
								</span>
              ))}
            </div>
          )}

          <h2 className="uikit-heading-md agenda-item__title">{title}</h2>
          {isBreakLine && text && (
            <p className="uikit-text-regular agenda-item__text">{ReactHtmlParser(text, {
              transform: transformSource,
            })}
            </p>)}
        </div>
        {speakers && (
          <div className="agenda-item__people">
            {speakers?.length > 3 ? (
              <div className="agenda-item__inline-people">
                {speakers?.map((speaker) => {
                  const {
                    speakerName,
                    speakerPreviewDescription,
                    speakerImage,
                    _url: url,
                    location: speakerLocation
                  } = speaker || {};
                  return <>
                    {url && (
                      <Link
                        to={url}
                        key={speakerImage?.src}
                      >
                        <OverlayTrigger
                          key="bottom"
                          placement="bottom"
                          overlay={
                            <Tooltip id="tooltip-bottom">
                              <strong>{speakerName}</strong>
                              <span>{speakerPreviewDescription}</span>
                            </Tooltip>
                          }
                        >
                          <div
                            className="agenda-item__inline-person agenda-inline-person"
                            key={speakerImage?.src}
                          >
                            {speakerImage && <Picture imageObj={speakerImage}/>}
                            {speakerLocation?.country?._url && (
                              <img
                                src={constructDevSrc(speakerLocation?.country?._url)}
                              />)}
                          </div>
                        </OverlayTrigger>
                      </Link>
                    )}
                  </>
                })}
              </div>
            ) : (
              speakers?.map((speaker) => {
                const {
                  speakerName,
                  speakerImage,
                  location: speakerLocation,
                  _url: url,
                  speakerPreviewDescription
                } = speaker || {};
                return (
                  <div
                    className="agenda-item__person agenda-person"
                    key={speakerName}
                  >
                    {url && (
                      <Link
                        to={url}
                      >
                        <div className="agenda-person__avatar">
                          {speakerImage && <Picture imageObj={speakerImage}/>}
                          {speakerLocation?.country?._url && (
                            <img
                              src={constructDevSrc(speakerLocation?.country?._url)}
                            />)}
                        </div>
                      </Link>)}
                    <div className="agenda-person__content">
                      {url && (
                        <Link
                          to={url}
                        >
                          <span className="agenda-person__name">
                            {speakerName}
                          </span>
                        </Link>)}
                      {speakerPreviewDescription && (
                        <span className="agenda-person__job">
                          {speakerPreviewDescription}
                        </span>)}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        )}

        {text && !isBreakLine &&
          <Accordion defaultActiveKey="0">
            <Card>
              <Card.Header>
                <CustomToggle
                  eventKey="1"
                  showMoreButton={showMoreButton}
                  setShoeMoreButton={setShoeMoreButton}
                >
                  {showMoreButton ? <span>Show More</span> : <span>Hide</span>}

                  <ArrowToggler/>
                </CustomToggle>
              </Card.Header>
              <Accordion.Collapse eventKey="1">
                <Card.Body>
                  <div className="agenda-item__description uikit-typo-container">
                    <h3 className="uikit-heading-tiny">
                      What we will talk about:
                    </h3>
                    <p className="uikit-text-regular">{ReactHtmlParser(text, {
                      transform: transformSource,
                    })}</p>
                  </div>
                </Card.Body>
              </Accordion.Collapse>
            </Card>
          </Accordion>}
      </div>
    </div>
  );
}

export default AgendaItem;

type CustomToggleProps = {
  showMoreButton: boolean;
  setShoeMoreButton: (isShown: boolean) => void;
  children: React.ReactNode;
  eventKey: string;
};

function CustomToggle(props: CustomToggleProps) {
  const decoratedOnClick = useAccordionToggle(props.eventKey, () => {
    props.setShoeMoreButton(!props.showMoreButton);
  });

  return (
    <span
      onClick={decoratedOnClick}
      aria-hidden="true"
      className={`agenda-item__toggler ${
        props.showMoreButton ? '' : 'agenda-item__toggler--active'
      }`}
    >
			{props.children}
		</span>
  );
}
